import { useErrorStore } from 'stores/error-store';

/**
 * Scopes a page's errors and redirects to the navigation that started its work.
 *
 * @param {Object} [options] - The page prefetch context, or redirect/store options for a mounted page action.
 * @returns {{raiseError: Function, redirect: Function}} - Navigation-scoped page effects.
 */
export function createPageNavigation({ currentRoute, redirect, ssrContext, store } = {}) {
    const isCurrent = useErrorStore(store).captureNavigation(currentRoute);
    return { raiseError, redirect: redirectIfCurrent };

    function raiseError(statusCode) {
        if (isCurrent()) {
            raiseErrorPage(statusCode, { ssrContext, store });
        }
    }

    function redirectIfCurrent(...args) {
        if (isCurrent()) {
            return redirect(...args);
        }
    }
}

/**
 * Answers the current URL with an error instead of the page that was asked for.
 *
 * The error is raised in place rather than redirected to, so the status lands on the URL that failed and a crawler
 * is told what it asked about instead of being sent to an error route. On the server the status is also handed to the
 * render middleware, which is what turns it into the response status; the store half is what the browser hydrates
 * against, so both sides render the same thing at the same address.
 *
 * A status that has no error screen of its own, such as the 403 a profile error route carries, leaves the matched
 * route rendering and only changes the status the response carries.
 *
 * @param {number} statusCode - The terminal status the response has to carry.
 * @param {Object} [options] - The options preFetch was called with, or the SSR context alone from a router guard.
 * @param {Object} [options.store] - The active Pinia instance, absent when the active one can be used.
 * @param {Object} [options.ssrContext] - The SSR context, absent when running in the browser.
 */
export function raiseErrorPage(statusCode, { store, ssrContext } = {}) {
    useErrorStore(store).raise(statusCode);
    if (ssrContext) {
        ssrContext.errorStatusCode = statusCode;
    }
}

/**
 * Drops a raised error so the next navigation renders the page it asked for.
 *
 * @param {Object} [options] - The options preFetch was called with, or the SSR context alone from a router guard.
 * @param {Object} [options.store] - The active Pinia instance, absent when the active one can be used.
 * @param {Object} [options.ssrContext] - The SSR context, absent when running in the browser.
 */
export function clearErrorPage({ store, ssrContext } = {}) {
    useErrorStore(store).clear();
    if (ssrContext) {
        ssrContext.errorStatusCode = null;
    }
}
